import { useScrollReveal } from '../../hooks/useScrollReveal';

export interface ProcessStep {
  number: string;
  icon: string;
  title: string;
  description: string;
}

interface PageProcessProps {
  headline?: string;
  accentLine?: string;
  steps?: ProcessStep[];
}

const defaultSteps: ProcessStep[] = [
  {
    number: '01',
    icon: 'ri-edit-2-line',
    title: 'Request a Quote or Book Online',
    description: 'Provide your trip details in seconds — pickup location, destination, date, and time. No complicated forms.',
  },
  {
    number: '02',
    icon: 'ri-check-double-line',
    title: 'Confirm Your Ride',
    description: 'Receive instant confirmation with clear, transparent pricing. No hidden fees, no surprises — ever.',
  },
  {
    number: '03',
    icon: 'ri-car-fill',
    title: 'Professional Pickup',
    description: 'Your chauffeur arrives early, ready to go, in a clean luxury vehicle — every single time.',
  },
  {
    number: '04',
    icon: 'ri-star-smile-fill',
    title: 'Smooth, Comfortable Ride',
    description: 'Sit back and enjoy a safe, efficient, luxury experience from your door to your destination.',
  },
];

export default function PageProcess({
  headline = 'Simple, Reliable',
  accentLine = 'Booking Process',
  steps = defaultSteps,
}: PageProcessProps) {
  const ref = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-28" style={{ backgroundColor: '#0d0d0d' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className="section-reveal">
          <div className="text-center mb-16">
            <span className="text-gold text-xs tracking-widest uppercase font-semibold">How It Works</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-3">
              {headline}<br />
              <span className="text-gold">{accentLine}</span>
            </h2>
            <div className="h-0.5 w-16 bg-gold mx-auto mt-6"></div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 relative">
            {steps.map((step, i) => (
              <div key={step.number} className="relative group">
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-1/2 w-full h-px bg-gradient-to-r from-gold/30 to-transparent z-0"></div>
                )}
                <div className="relative z-10 flex flex-col items-center text-center px-6 pb-10">
                  <div className="relative mb-6">
                    <div className="w-24 h-24 rounded-full bg-neutral-900 border-2 border-gold/25 group-hover:border-gold flex flex-col items-center justify-center transition-all duration-300 group-hover:bg-neutral-800">
                      <div className="w-7 h-7 flex items-center justify-center">
                        <i className={`${step.icon} text-gold text-xl`}></i>
                      </div>
                    </div>
                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-gold flex items-center justify-center">
                      <span className="text-black text-xs font-bold">{step.number}</span>
                    </div>
                  </div>
                  <h3 className="text-white font-semibold text-sm mb-3 leading-snug group-hover:text-gold transition-colors">{step.title}</h3>
                  <p className="text-white/40 text-xs leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-6">
            <a
              href="tel:+18436422440"
              className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light transition-colors text-black font-bold px-12 py-4 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill text-sm"></i></div>
              Call (843) 642-2440 to Book
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
